import { styled as styledFromMUI } from '@material-ui/core/styles';
import { css, SerializedStyles } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Button,
  IconButton,
  TextField,
  Typography,
  Chip,
} from '@material-ui/core';

import { ICustomTheme } from 'interfaces';

type Theme = {
  theme: ICustomTheme;
};

export const ArrowButton = styled(Button)<Theme>(({ theme }) => ({
  display: 'flex',
  borderRadius: '100%',
  color: theme.colors.white,
  height: 40,
  minWidth: 0,
  padding: 0,
  width: 40,
  boxShadow: theme.shadows[3],
}));

export const AddButton = styled(IconButton)<Theme>(({ theme }) => ({
  backgroundColor: theme.colors.paletteFromBO.primary,
  borderRadius: theme.shape.borderRadius,
  color: theme.colors.white,
  position: 'absolute',
  bottom: theme.spacing(1),
  left: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.colors.paletteFromBO.primary,
  },
}));

export const Wrapper = styled.div<Theme>(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.white,
  borderRadius: theme.shape.borderRadius * 10,
  bottom: '2%',
  boxShadow: theme.shadows[3],
  display: 'flex',
  height: 80,
  justifyContent: 'space-around',
  left: '50%',
  minWidth: 300,
  padding: theme.spacing(2, 3),
  position: 'fixed',
  transform: 'translateX(-50%)',
  zIndex: 9999,
}));

export const cssButtonContainer = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const cssCustomStepper = (theme: ICustomTheme): SerializedStyles =>
  css({
    '& .MuiStep-completed': {
      '& .MuiStepConnector-line': {
        background: theme.colors.paletteFromBO.main,
      },
    },
  });

export const cssPanel = (theme: ICustomTheme): SerializedStyles =>
  css({
    boxShadow: 'none',
    margin: '0 !important',
    '::before': {
      backgroundColor: theme.palette.grey[100],
      opacity: '1 !important',
    },
  });

export const CardInput = styled(TextField)<{
  theme: ICustomTheme;
  bottomLabel: boolean;
}>(({ theme, bottomLabel }) => ({
  width: '50%',
  textAlign: 'center',
  input: {
    border: `1px solid ${theme.colors.paletteFromBO.main}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    textAlign: 'center',
    fontSize: 18,
    fontWeight: theme.typography.fontWeightBold,
    '&[type=number]': {
      '-moz-appearance': 'textfield',
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  label: {
    width: '175%',
    paddingBottom: theme.spacing(0.5),
    position: 'absolute',
    left: '50%',
    transform: bottomLabel
      ? `translate(-50%, ${theme.spacing(5)}px)`
      : `translate(-50%, ${theme.spacing(-4)}px)`,
    fontSize: theme.typography.caption.fontSize,
    fontStyle: 'italic',
    color: `${theme.colors.black.main} !important`,
  },
}));

export const EditionPaper = styled.div<Theme>(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  background: 'rgba(0, 0, 0, 0.4)',
  width: `calc(100% - ${theme.spacing(2)}px)`,
  position: 'absolute',
  bottom: 0,
  display: 'flex',
  margin: theme.spacing(1),
  padding: theme.spacing(1),
  color: theme.colors.white,
}));

export const EditButton = styled(IconButton)<Theme>(({ theme }) => ({
  backgroundColor: theme.colors.paletteFromBO.secondary,
  borderRadius: theme.shape.borderRadius,
  color: theme.colors.white,
  '&:hover': {
    backgroundColor: theme.colors.paletteFromBO.secondary,
  },
}));

export const ResetButton = styled(IconButton)<Theme>(({ theme }) => ({
  backgroundColor: theme.colors.grey.light5,
  borderRadius: theme.shape.borderRadius,
  color: theme.colors.white,
  '&:hover': {
    backgroundColor: theme.colors.grey.light5,
  },
}));

export const cssProductDescription = (theme: ICustomTheme): SerializedStyles =>
  css({
    color: theme.colors.grey.light2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '-webkit-line-clamp': '3',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
  });

export const cssResults = (theme: ICustomTheme): SerializedStyles =>
  css({
    backgroundColor: theme.colors.paletteFromBO.primary,
    borderRadius: 50,
    color: theme.colors.white,
  });

export const cssTitle = (theme: ICustomTheme): SerializedStyles =>
  css({
    [theme.breakpoints.down('sm')]: {
      color: theme.colors.paletteFromBO.main,
      fontSize: theme.typography.h5.fontSize,
      marginBottom: theme.spacing(2),
    },
  });

export const StyledTypography = styled(Typography)<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
}));

export const StyledContainer = styledFromMUI('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    flex: '1 0 auto',
    margin: '0 auto',
    paddingBottom: theme.spacing(6),
    paddingTop: theme.spacing(6),
    width: '80%',
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
    },
  }),
);

export const StyledChip = styled(Chip)<Theme>(({ theme }) => ({
  height: 'auto',
  backgroundColor: theme.colors.paletteFromBO.secondary,
}));
